import { Box, Button, Grid, Typography } from '@material-ui/core';
import { BlockOutlined, CheckCircleOutline } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { SignatureField, SignatureFieldHandle } from '@roc/ui';

export interface SigningInfo {
  date?: string;
  loanId?: number;
  servicerId?: string;
  lenderLine?: string;
  borrowersLine?: string;
  loanAmount?: string;
  collateralAddresses?: string[];
  currentMaturity?: string;
  noteDate?: string;
  newMaturityDate?: string;
  extensionFee?: string;
  borrowerName?: string;
  borrowerEntityName?: string;
  dealName?: string;
  alreadySigned?: boolean;
  hasSigningRequest?: boolean;
  signatureStatus?: string;
  voided?: boolean;
  canSign?: boolean;
  unavailableReason?: string | null;
}

interface Props {
  info: SigningInfo | null;
  signatureRef: React.RefObject<SignatureFieldHandle>;
  onSubmit: () => void;
  submitting?: boolean;
  error?: string;
}

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  paragraph: {
    fontSize: '0.85rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  submitButton: {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    width: '25%',
  },
  statusCard: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(6),
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  unavailableIcon: {
    fontSize: 64,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));

export const ExtensionAgreementSignedMessage = ({ borrowerName }: { borrowerName?: string }) => {
  const classes = useStyles();
  return (
    <Box className={classes.statusCard}>
      <CheckCircleOutline className={classes.successIcon} />
      <Typography variant="h5" style={{ fontWeight: 700 }}>
        Thank you{borrowerName ? `, ${borrowerName}` : ''}!
      </Typography>
      <Typography variant="body1" style={{ marginTop: 12 }}>
        Your Extension Agreement has been signed successfully.
      </Typography>
    </Box>
  );
};

export const ExtensionAgreementUnavailableMessage = ({ message }: { message?: string | null }) => {
  const classes = useStyles();
  return (
    <Box className={classes.statusCard}>
      <BlockOutlined className={classes.unavailableIcon} />
      <Typography variant="h5" style={{ fontWeight: 700 }}>
        Extension Agreement unavailable
      </Typography>
      <Typography variant="body1" style={{ marginTop: 12 }}>
        {message ||
          'This Extension Agreement is no longer available for signature. Please contact your lender.'}
      </Typography>
    </Box>
  );
};

export const ExtensionAgreementContent = ({ info, signatureRef, onSubmit, submitting, error }: Props) => {
  const classes = useStyles();

  return (
    <Box width="100%">
      <Typography className={classes.title}>Extension Agreement</Typography>

      <Typography className={classes.paragraph}>
        <b>Date:</b> {info?.date}
      </Typography>
      <Typography className={classes.paragraph}>
        <b>Lender Loan#:</b> {info?.loanId} &nbsp; <b>SLS Loan#:</b> {info?.servicerId}
      </Typography>
      <Typography className={classes.paragraph}>
        <b>Lender:</b> {info?.lenderLine}
      </Typography>
      <Typography className={classes.paragraph}>
        <b>Borrower/Guarantor(s):</b> {info?.borrowersLine}
      </Typography>
      <Typography className={classes.paragraph}>
        <b>Loan Amount:</b> {info?.loanAmount}
      </Typography>
      <Typography className={classes.paragraph}>
        <b>Collateral Address(es):</b>
        <br />
        {(info?.collateralAddresses ?? []).map((address, i) => (
          <span key={i}>
            {address}
            <br />
          </span>
        ))}
      </Typography>
      <Typography className={classes.paragraph}>
        <b>Current Maturity:</b> {info?.currentMaturity}
      </Typography>

      <Typography className={classes.paragraph}>
        NOW, Borrower executed a Promissory Note dated {info?.noteDate}, in favor of Lender in the
        Loan Amount (the &ldquo;Note&rdquo;) and secured by a Mortgage of the same date (the
        &ldquo;Mortgage&rdquo;).
      </Typography>
      <Typography className={classes.paragraph}>
        NOW, under the Note, the entire loan is due and payable in full on the {info?.currentMaturity},
        and, to date, Borrower has not paid the Loan Amount in full.
      </Typography>
      <Typography className={classes.paragraph}>
        NOW, Lender has agreed to extend Borrower&rsquo;s time to satisfy the Note until{' '}
        {info?.newMaturityDate}, upon compliance by Borrower of the following terms and conditions:
      </Typography>
      <Typography className={classes.paragraph}>
        1. Borrower agrees to pay to Lender upon payoff of the Note an extension fee in the amount of{' '}
        {info?.extensionFee}.
      </Typography>
      <Typography className={classes.paragraph}>
        2. Borrower agrees to comply with all of the other terms of the Note and other loan documents.
      </Typography>
      <Typography className={classes.paragraph}>
        Provided Borrower satisfies all of the terms herein, Borrower shall have until{' '}
        {info?.newMaturityDate}, to satisfy the Note. In the event Borrower fails to satisfy the Note
        by {info?.newMaturityDate}, interest will be accrued at the default rate under the terms of the
        Note and other loan documents.
      </Typography>
      <Typography className={classes.paragraph}>
        At Lender&rsquo;s sole discretion, Lender may instruct Superior Loan Servicing (&ldquo;SLS&rdquo;)
        to continue accepting payments from Borrower past {info?.newMaturityDate}, at the original terms
        stated in the Note and other loan documents, as hereby modified. Borrower authorizes SLS to either
        (A) initiate, and/or continue the initiation of, ACH direct debit transactions from Borrower&rsquo;s
        bank account supplied to SLS; and/or (B) otherwise accept monthly payments due and owing on the Note
        in the form of paper checks or as otherwise agreed by and between SLS and Borrower. If the Current
        Maturity Date of the Note has passed, the continued acceptance of payments by Lender is at the sole
        and absolute discretion of the Lender, and Lender is not obligated to accept such payments. Lender
        reserves the right to cease or discontinue acceptance of payments from Borrower at any time and to
        pursue all of its available remedies under the Note and other loan documents.
      </Typography>
      <Typography className={classes.paragraph}>
        All other terms of the Note, Mortgage and other loan documents will remain in full force and effect.
      </Typography>

      <Typography className={classes.paragraph} style={{ marginTop: 24 }}>
        <b>BORROWER:</b> {info?.borrowerName}
      </Typography>

      <SignatureField ref={signatureRef} penColor="green" />

      {error && (
        <Typography color="error" style={{ marginTop: 8 }}>
          {error}
        </Typography>
      )}

      <Grid container style={{ justifyContent: 'center' }}>
        <Button
          variant="contained"
          className={classes.submitButton}
          onClick={onSubmit}
          disabled={submitting || !info || info.canSign === false}
        >
          {submitting ? 'SUBMITTING...' : 'SUBMIT'}
        </Button>
      </Grid>
    </Box>
  );
};
